import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const SIGNED_TTL_SECONDS = 60 * 60 * 24 * 7; // 7 days

function getStorageHost() {
  try {
    return new URL(import.meta.env.VITE_SUPABASE_URL).host;
  } catch {
    return "";
  }
}

/**
 * Converts a Supabase Storage public URL into a signed URL when the bucket
 * is private. Falls back to the original URL for non-Storage URLs or when the
 * signed URL cannot be generated.
 *
 * This is used for objects that anonymous users are allowed to read via RLS
 * (e.g., live property images and manager logos) but that live in a private
 * bucket, so the raw public URL is not directly accessible.
 */
export function useSignedStorageUrl(publicUrl: string | null | undefined) {
  const [signedUrl, setSignedUrl] = useState<string | null>(publicUrl ?? null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!publicUrl) {
      setSignedUrl(null);
      return;
    }

    const storageHost = getStorageHost();
    if (!storageHost || !publicUrl.includes(storageHost)) {
      setSignedUrl(publicUrl);
      return;
    }

    let cancelled = false;

    const pathMatch = publicUrl.match(/\/storage\/v1\/object\/public\/([^/]+)\/(.+)$/);
    if (!pathMatch) {
      setSignedUrl(publicUrl);
      return;
    }

    const [, bucket, path] = pathMatch;

    supabase.storage
      .from(bucket)
      .createSignedUrl(path, SIGNED_TTL_SECONDS)
      .then(({ data, error: signError }) => {
        if (cancelled) return;
        if (signError) {
          setError(signError);
          setSignedUrl(publicUrl);
          return;
        }
        if (data?.signedUrl) {
          setSignedUrl(data.signedUrl);
        } else {
          setSignedUrl(publicUrl);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [publicUrl]);

  return { signedUrl, error };
}
